/* eslint-disable import/extensions */
import React, { useEffect, useState } from 'react';

import axios from 'axios';
import lexical from 'lexical';
import dynamic from 'next/dynamic';
import router from 'next/router';
import Embed from 'react-embed';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import editFile from '@/api/editFile';
import Editor from '@/components/Editor/Editor';
import { sendNotifcation } from '@/utils/notification';
import { ITextFileViewerProps } from 'interfaces/File/file';

import { PrimaryButton } from '../../Button/PrimaryButton';
import JSONViewer from '../JSONViewer/JSONViewer';

const ExcalidrawViewer = dynamic(
  () => import('../../Excalidraw/ExcalidrawViewer'),
  { ssr: false }
);

export default function TextFileViewer({
  fileData,
  setIsLexical,
  isEditClicked,
  setIsEditClicked,
}: ITextFileViewerProps) {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState<string>('Content');
  const [textValue, setTextValue] = useState<string>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [finalEditorState, setFinalEditorState] = useState<any>('');
  const [initialEditorState, setInitialEditorState] = useState<any>('');

  const { executeRecaptcha } = useGoogleReCaptcha();
  const fileId = router.query.fileId as string;

  const uploadEditFile = async () => {
    setUploading(true);

    const now = new Date();
    const documentJSON = {
      editorState: finalEditorState,
      lastSaved: now.getTime(),
      source: 'Lexical',
      version: lexical.VERSION,
    };

    const fileObj = {
      file: documentJSON,
      source: 'Lexical',
      version: lexical.VERSION,
    };

    let xfilename = '';
    if (fileName) {
      const nfilename = fileName.split('.');
      if (nfilename.length > 1) nfilename.pop();
      xfilename = nfilename.join('.');
    }

    const blob = new Blob([JSON.stringify(fileObj)], {
      type: 'application/json',
    });
    const newFile = new File(
      [blob],
      `${xfilename || 'Untitled-Document'}.json`,
      {
        type: 'application/json',
      }
    );

    const recaptchaToken = await executeRecaptcha('file');
    const result = await editFile({
      files: newFile,
      recaptchaToken,
      fileId,
    });
    sendNotifcation('File Uploaded Successfully', '', 'success');
    setUploading(false);
    router.push(`/files/${result?.data?.uuid}?ownerFirstVisit=true`);
  };

  useEffect(() => {
    if (uploading) {
      setIsEditClicked(false);
    }
  }, [uploading]);

  useEffect(() => {
    setFileName(fileData.name);
    axios.get(fileData.url).then((res) => {
      if (fileData.mimetype === 'application/octet-stream') {
        setTextValue(res.data);
        setFile({
          mimeType: 'text/markdown',
          source: 'Lexical',
          file: res.data,
          version: 1,
        });
        setIsLexical(true);
      } else {
        setFile(res.data);
        if (res.data.file && res.data.file.source === 'Lexical') {
          setInitialEditorState(res.data.file.editorState);
          setIsLexical(true);
        }
      }
    });
  }, []);

  if (file?.source === 'fileverse-excalidraw')
    return <ExcalidrawViewer file={file.file} />;

  if (file?.source === 'Link') {
    return (
      <div className="w-[100%] h-full p-4 overflow-y-auto">
        <Embed url={file.file} />
      </div>
    );
  }
  if (file?.source === 'Embed') {
    return (
      <div className="flex p-4 justify-center h-full items-center ">
        <div className="h-full relative w-full">
          <div
            className="embed-container"
            dangerouslySetInnerHTML={{ __html: file.file }}
          ></div>
        </div>
      </div>
    );
  }
  if (file?.source === 'Lexical')
    return (
      <div className={`w-full h-full ${isEditClicked ? 'pb-32' : ''}`}>
        <Editor
          textValue={textValue}
          fromViewer={true}
          isEditClicked={isEditClicked}
          fileName={fileName}
          setFileName={setFileName}
          setTextValue={setTextValue}
          setFinalEditorState={setFinalEditorState}
          initialEditorState={initialEditorState}
        />
        {isEditClicked && (
          <div className="items-center grid grid-cols-2 bottom-4 left-4 right-4 absolute bg-white">
            <div>
              <PrimaryButton onClick={() => setIsEditClicked(false)}>
                <span>Back </span>
              </PrimaryButton>
            </div>
            <div className="flex justify-end">
              <PrimaryButton onClick={uploadEditFile}>
                <span>Done </span>
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    );

  return <JSONViewer file={file} />;
}
